import { Capacitor } from '@capacitor/core';

import {
  applyCropPictureHeaderOverlay,
  CropPictureHeaderMeta,
} from './crop-picture-header';
import { saveLocalCropPictureArchive } from './local-crop-picture-archives';
import {
  isMobileGalleryArchiveAvailable,
  saveCropPictureViaDownload,
  saveCropPictureToGallery,
} from './save-crop-picture-to-gallery';

export type OfflineCropPictureArchiveResult = {
  file: File;
  savedToDevice: boolean;
  needsUserSave: boolean;
};

function isMobileWebArchive(): boolean {
  return !Capacitor.isNativePlatform() && isMobileGalleryArchiveAvailable();
}

/**
 * Prepares and stores a stamped HL security copy.
 * Runs before network waits so PWA user-gesture chains stay as fresh as possible.
 */
export async function prepareOfflineCropPictureArchive(
  file: File,
  meta: CropPictureHeaderMeta
): Promise<OfflineCropPictureArchiveResult | null> {
  if (!isMobileGalleryArchiveAvailable()) {
    return null;
  }

  try {
    const stamped = await applyCropPictureHeaderOverlay(file, meta);
    await saveLocalCropPictureArchive(stamped, meta);

    if (Capacitor.isNativePlatform()) {
      const savedToDevice = await saveCropPictureToGallery(stamped);
      return { file: stamped, savedToDevice, needsUserSave: false };
    }

    const savedToDevice = await saveCropPictureViaDownload(stamped);
    return {
      file: stamped,
      savedToDevice,
      needsUserSave: !savedToDevice && isMobileWebArchive(),
    };
  } catch {
    return null;
  }
}

/** @deprecated Use prepareOfflineCropPictureArchive from picture-input. */
export async function archiveCropPictureForOfflineMode(
  file: File,
  meta: CropPictureHeaderMeta
): Promise<void> {
  await prepareOfflineCropPictureArchive(file, meta);
}
